import * as React from "react"
import { useState, useRef, useEffect } from "react"
import { TouchpadInput, hasTouchScreen } from "./TouchpadInput"

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

interface QuestionTextInputProps {
  question: {
    num1: number;
    num2: number;
    operator: string;
    answer: number;
  };
  onAnswer: (answer: number, typingData?: { count: number; time: number }) => void;
  showFeedback: boolean;
}

export const QuestionTextInput: React.FC<QuestionTextInputProps> = ({ question, onAnswer, showFeedback }) => {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Add refs for keystroke timing
  const firstKeystrokeTime = useRef<number | null>(null);
  const lastKeystrokeTime = useRef<number | null>(null);

  const calculateTypingData = (isCorrect: boolean) => {
    if (!isCorrect || !firstKeystrokeTime.current || !lastKeystrokeTime.current) {
      return undefined;
    }

    const answerLength = question.answer.toString().length;
    if (answerLength <= 1) {
      return undefined;
    }

    const totalTypingTime = lastKeystrokeTime.current - firstKeystrokeTime.current;
    
    // For 3-digit numbers, we have 2 inter-digit intervals, so divide by 2
    const averageTypingTime = answerLength === 3 ? totalTypingTime / 2 : totalTypingTime;

    return {
      count: 1, // Each answer counts as 1 attempt
      time: averageTypingTime
    };
  };

  useEffect(() => {
    setValue('');
    firstKeystrokeTime.current = null;
    lastKeystrokeTime.current = null;
  }, [question]);

  useEffect(() => {
    if (inputRef.current && !showFeedback) {
      inputRef.current.focus();
    }
  }, [showFeedback]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!value || showFeedback) return;

    const numValue = parseInt(value);
    if (!isNaN(numValue)) {
      const isCorrect = numValue === question.answer;
      onAnswer(numValue, calculateTypingData(isCorrect));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleInputChange = (newValue: string) => {
    const isMultiDigitAnswer = question.answer.toString().length > 1;

    // Track keystroke timing for multi-digit answers
    if (isMultiDigitAnswer) {
      const now = Date.now();
      
      // Record first keystroke
      if (newValue.length === 1) {
        firstKeystrokeTime.current = now;
      }
      
      // Record subsequent keystrokes
      if (newValue.length > 0) {
        lastKeystrokeTime.current = now;
      }
    }

    setValue(newValue);

    // Auto-submit when input length matches answer length
    if (newValue.length === question.answer.toString().length) {
      const numValue = parseInt(newValue);
      if (!isNaN(numValue)) {
        const isCorrect = numValue === question.answer;
        onAnswer(numValue, calculateTypingData(isCorrect));
      }
    }
  };

  const handleNativeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, '');
    handleInputChange(newValue);
  };

  return (
    <div className={hasTouchScreen ? "flex justify-between" : "flex justify-center"}>
      {/* Left side - Question and input */}
      <div className="flex flex-col items-center justify-center flex-1">
        <p className="text-6xl font-bold text-gray-800 mb-8">
          {question.num1} {question.operator} {question.num2} = ?
        </p>

        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            value={value}
            onChange={handleNativeInputChange}
            onKeyDown={handleKeyPress}
            disabled={showFeedback}
            className={`
              w-48 
              p-4 
              text-4xl 
              font-bold 
              text-center 
              rounded-lg
              border-4
              outline-none
              transition-all
              duration-200
              ${!showFeedback
                ? 'border-gray-200 focus:border-gray-200 bg-white text-gray-700'
                : 'border-blue-500 bg-blue-50 text-blue-700'
              }
            `}
          />
        </div>
      </div>

      {/* Right side - Touchpad */}
      {hasTouchScreen && (
        <TouchpadInput
          value={value}
          onChange={handleInputChange}
          disabled={showFeedback}
          showFeedback={showFeedback}
        />
      )}
    </div>
  );
};

export { Input }